import React from 'react';
import { useIntl } from 'react-intl';
import { WHITE } from 'shared/constants/colors';
import { sum, percent } from 'shared/constants/math';

import Chart from './Chart';
import { formatSegment, labelFormatter, tooltipFormatter } from './formatters';
import {
  iconBase,
  colorChartParts,
  uniqueSegmentId,
  getSegment,
  mergeValues,
  getGridOptions,
  getValueAxis,
  getUniqueValues,
} from './utils';

const StackedBarChart = (props) => {
  const {
    segments,
    question,
    isScoreGraph,
    chartSettings = {},
    onChartSettingsChange,
    toolbox,
  } = props;
  const { formatMessage, locale } = useIntl();

  const { displayMode, switchedAxis } = chartSettings;

  const showAsPercentage = displayMode === 'percentage' && !isScoreGraph;

  const segmentValues = segments.filter(s => isScoreGraph || !s.isBenchmark).map((segment) => {
    const values = mergeValues(segment, question, formatMessage);
    return {
      name: uniqueSegmentId(segment),
      values,
      sum: sum(values.map(v => v.value)),
    };
  });

  const values = colorChartParts(question, getUniqueValues(segmentValues));

  const series = values.map(({ name, itemStyle, label }) => ({
    name,
    type: 'bar',
    stack: 'stack',
    barMaxWidth: 150,
    label: {
      show: true,
      position: 'inside',
      color: WHITE,
      formatter: labelFormatter(showAsPercentage, 3),
      ...label,
    },
    itemStyle,
    data: segmentValues.map((segmentValue) => {
      const { value: numericValue, ...rest } = segmentValue.values.find(v => v.name === name) || {};
      const percentageValue = percent(numericValue, segmentValue.sum);

      const value = showAsPercentage ? percentageValue : numericValue;

      return {
        ...rest,
        segmentName: segmentValue.name,
        numericValue,
        percentageValue,
        value: switchedAxis ? [segmentValue.name, value] : [value, segmentValue.name],
      };
    }),
  }));

  const valueAxis = getValueAxis({ showAsPercentage, min: showAsPercentage ? 0 : null, max: showAsPercentage ? 100 : null });

  const getCategoryAxis = () => {
    const data = segmentValues.map(v => v.name);

    // => '{icon000|} Segment 1 (100)'
    const formatter = (value) => {
      const segment = getSegment(segments, value);
      const color = segment.color?.replace('#', '');

      const formatted = formatSegment(segment, { formatMessage, locale });
      return `{icon${color}|} ${formatted}`;
    };

    // For each segment color add a prop to the rich object
    const rich = {}; // => { icon000: { backgroundColor: #000 } }
    segments
      .map(segment => segment.color)
      .filter(x => x)
      .forEach((color) => {
        const key = color.replace('#', '');
        rich[`icon${key}`] = { ...iconBase, backgroundColor: color };
      });

    return {
      type: 'category',
      data,
      inverse: !switchedAxis,
      axisLabel: {
        interval: 0,
        lineHeight: 16,
        formatter,
        rich,
      },
    };
  };

  const categoryAxis = getCategoryAxis();

  const tooltip = {
    trigger: 'item',
    formatter: ({ seriesName, data }) => {
      const formattedValue = tooltipFormatter({ data });
      const formattedSegment = formatSegment(getSegment(segments, data.segmentName), { lineBreak: '<br />', formatMessage, locale, ...props });

      return `${seriesName}: ${formattedValue}<br />${formattedSegment}`;
    },
  };

  const option = {
    grid: getGridOptions(),
    xAxis: switchedAxis ? categoryAxis : valueAxis,
    yAxis: switchedAxis ? valueAxis : categoryAxis,
    series,
    tooltip,
    legend: {
      data: [...new Set(values.map(v => v.name))],
    },
  };

  const height = switchedAxis ? undefined : segments.length * 30 + 300;

  return (
    <Chart
      {...props}
      height={height}
      option={option}
      toolbox={{
        ...toolbox,
        switchAxis: () => onChartSettingsChange({ switchedAxis: !switchedAxis }),
      }}
    />
  );
};

export default StackedBarChart;
